import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FlagCountResponse,
  GroupCount,
  TopicCount,
  ItemsForReviewRequest,
  ItemsForReviewSortCriteria,
  ItemsForSummaryRequest,
  ItemWithMetaData,
} from "../api/Items";
import {
  useFetchFlagCount,
  useFetchItemsForSummary,
  useFetchKeywordsCount,
  useFetchTopicsCount,
} from "../api/ItemsApi";
import { ReportSummaryItemApi, SourceTypeFilter } from "../api/Reports";
import { useLocation, useNavigate } from "react-router-dom";
import PDFDownloadWrapper from "./PDFDownloadWrapper";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import {
  useFetchReportRerun,
  usePostReportReviewMark,
  useReportAmendment,
} from "../api/ReportsApi";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { useReportUnlock } from "./useReportUnlock";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import DropdownCombo from "../shared-components/DropdownCombo";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  buildReportItemsFilterRequests,
  getColorCode,
  processFlagCountForDisplay,
} from "./utils";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useReportDetail } from "./ReportDetailProvider";
import { PopMenu } from "../shared-components/PopMenu";
import ElipseIcon from "../shared-components/icons/ElipseIcon";
import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardIcon,
  DocumentIcon,
  ShieldExclamationIcon,
  ArrowPathRoundedSquareIcon,
} from "@heroicons/react/24/outline";
import { getRerunParentReportUuid, isRerunReport } from "./reportRerunUtils";
import ReportsSummaryRerunBanner from "./ReportsSummaryRerunBanner";
import { classNames, useDebounce } from "../utils";
import {
  countFilterKey,
  reviewRequestMatchesFilters,
  trackCountResponses,
  useFilteredCountGeneration,
} from "./ReportSidebar/useFilteredCountGeneration";
import useRegeneratePDFWithToast from "./PDFRegenerationWrapper";
import useNavigateNewPerson from "./useNavigateNewPerson";
import { useFamaFlags } from "../api/FamaFlags";
import RecurCreateWrapper from "../recur/RecurCreateWrapper";
import { ReportsSummaryHeader } from "./helper-components/ReportsSummaryHeader";
import ReportsSummaryPosts from "./helper-components/ReportsSummaryPosts";
import { renderReportMenuItem } from "./renderReportMenuItem";

type ReportSummaryProps = {
  sourceTypeFilter: SourceTypeFilter;
  postTypeFilter: { original: boolean; shared: boolean; liked: boolean };
  dateFilter: { fromDate: string; toDate: string };
  behaviorFilter: string[];
  keywordFilter: string[];
  topicFilter: string[];
  itemTypeFilter: string[];
  updateFlagCount: (flags: FlagCountResponse) => void;
  updateKeywordCount: (keywords: GroupCount) => void;
  updateTopicCount?: (topics: TopicCount) => void;
  report: ReportSummaryItemApi;
  summaryOnly?: boolean;
};

const INITIAL_PAGE_SIZE = 100;
const pageSize = 20;
const LOAD_MORE_INITIAL_PAGE = 5;

const ReportsSummary = ({
  sourceTypeFilter,
  postTypeFilter,
  dateFilter,
  behaviorFilter,
  keywordFilter,
  topicFilter,
  itemTypeFilter,
  updateFlagCount,
  updateKeywordCount,
  updateTopicCount,
  report,
  summaryOnly,
}: ReportSummaryProps) => {
  const navigate = useNavigate();
  const { navigateCreatePerson } = useNavigateNewPerson();
  const [shouldLoadMore, setShouldLoadMore] = useState(true);

  const location = useLocation();
  const {
    canAmend,
    canRerunReport,
    canNavAmendment,
    canPrintCurrentView,
    canRegeneratePDF,
    person,
  } = usePermissionValidator();

  const { isLoadingProfiles, profileList, setFilteredCountsReady } =
    useReportDetail();
  const [recurReport, setRecurReport] = useState<ReportSummaryItemApi | null>(
    null
  );
  const [isRecurOpen, setIsRecurOpen] = useState(false);

  const {
    isLoading: postsLoading,
    mutateAsync: getPostsAsync,
    error: postError,
  } = useFetchItemsForSummary();

  const {
    isLoading: flagsLoading,
    data: flagsCount,
    mutate: getFlags,
    error: errorFlag,
  } = useFetchFlagCount();

  const {
    isLoading: keywordCountLoading,
    data: keywordCount,
    mutate: getKeywordCount,
    error: errorKeywordCount,
  } = useFetchKeywordsCount();

  const {
    isLoading: topicCountLoading,
    data: topicCount,
    mutate: getTopicCount,
    error: errorTopicCount,
  } = useFetchTopicsCount();

  const { data: reportReruns, mutate: getReruns } = useFetchReportRerun();

  const rerunParentUuid = useMemo(
    () => getRerunParentReportUuid(report, reportReruns),
    [report, reportReruns]
  );

  const {
    isLoading: loadingAmendment,
    data: amendmentData,
    mutate: openAmendment,
    error: errorAmendment,
  } = useReportAmendment();

  const { regeneratePDF, errorGeneratePDF, loadingGeneratePDF } =
    useRegeneratePDFWithToast();

  const [filteredPostsList, setFilteredPostsList] = useState<
    ItemWithMetaData[] | undefined
  >([]);
  const {
    isSuccess: isSuccessMark,
    mutate: markReviews,
    error: errorMarkReviews,
    isLoading: loadingMarkReviews,
  } = usePostReportReviewMark();
  const [page, setPage] = useState(1);
  const { hide, modal, show } = useConfirmationDialog();
  const { behaviorMappingActions } = useFamaFlags();
  const [sort, setSort] = useState<{ id: string; label: string }>({
    id: ItemsForReviewSortCriteria.Date,
    label: "Sort By Date",
  });

  const {
    isLoading: loadingUnlockReport,
    error: errorUnlockingReport,
    modal: modalUnlock,
  } = useReportUnlock();

  const navigateToReports = useCallback(() => {
    const originUrl = (location.state as { originUrl: string } | null)
      ?.originUrl;
    if (originUrl) {
      navigate(originUrl);
      return;
    }

    // If there's no origin URL, determine where to navigate based on report status
    if (report?.step_name) {
      if (report.step_name.includes("INTERNAL_QA")) {
        navigate("/reports/internal_qa");
        return;
      } else if (report.step_name.includes("CLIENT_QA")) {
        navigate("/reports/client_qa");
        return;
      } else if (report.step_name.includes("PENDING_REVIEW")) {
        navigate("/reports/pending_review");
        return;
      }
    }

    // Default to in-process reports if we can't determine where to go
    navigate("/reports/in_process");
  }, [location.state, navigate, report]);

  const [flagAmounts, setFlagAmounts] = useState<{ [key: string]: number }>({});
  const [postListCategory, setPostListCategory] = useState<{
    [key: string]: ItemWithMetaData[];
  }>({});
  const [requestParameters, setRequestParameters] =
    useState<ItemsForReviewRequest>({
      report_uuid: report?.report_uuid ?? "",
      item_type_name: [], // TODO: Check if the pie chart stills accepts no filter to return all
      page_size: pageSize,
      sort_criteria: ItemsForReviewSortCriteria.Date,
    });

  const [requestParametersSummary, setRequestParametersSummary] =
    useState<ItemsForSummaryRequest>({
      report_id: report.report_uuid ?? "",
      item_types: itemTypeFilter,
      behaviors: behaviorFilter,
      keywords: keywordFilter,
      topics: topicFilter,
      sort: sort.id,
      page_size: pageSize,
      page_number: page,
    });

  const debouncedRequestParametersSummary = useDebounce<ItemsForSummaryRequest>(
    requestParametersSummary,
    1000
  );
  const countFilters = countFilterKey({
    reportUuid: report.report_uuid,
    sourceTypeFilter,
    postTypeFilter,
    dateFilter,
    behaviorFilter,
    keywordFilter,
    topicFilter,
    itemTypeFilter,
  });
  const { ready: filteredCountsReady, capture, settle } =
    useFilteredCountGeneration(countFilters);

  useEffect(() => {
    setFilteredCountsReady?.(filteredCountsReady);
  }, [filteredCountsReady, setFilteredCountsReady]);

  useEffect(() => {
    if (!report) return;
    getReruns(report.report_uuid);
    if (
      !reviewRequestMatchesFilters(requestParameters, {
        behaviorFilter,
        keywordFilter,
        topicFilter,
        itemTypeFilter,
        sourceTypeFilter,
        postTypeFilter,
        dateFilter,
      })
    ) {
      return;
    }
    const responses = trackCountResponses(capture(), settle);
    getFlags(
      { ...requestParameters, report_uuid: report.report_uuid },
      responses
    );
    getKeywordCount(
      { ...requestParameters, report_uuid: report.report_uuid },
      responses
    );
    getTopicCount(
      { ...requestParameters, report_uuid: report.report_uuid },
      responses
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getFlags,
    getKeywordCount,
    getTopicCount,
    getReruns,
    report,
    debouncedRequestParametersSummary,
  ]);

  useEffect(() => {
    if (!report) return;
    const { review, summary } = buildReportItemsFilterRequests({
      report,
      itemTypeFilter,
      behaviorFilter,
      keywordFilter,
      topicFilter,
      sortId: sort.id,
      pageSize,
      summaryPageNumber: 1,
      sourceTypeFilter,
      postTypeFilter,
      dateFilter,
    });
    setRequestParameters(review);
    setRequestParametersSummary(summary);
  }, [
    sourceTypeFilter,
    postTypeFilter,
    dateFilter,
    behaviorFilter,
    keywordFilter,
    topicFilter,
    itemTypeFilter,
    sort,
    report,
  ]);

  useEffect(() => {
    loadPosts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRequestParametersSummary]);

  useEffect(() => {
    if (!flagsCount) return;
    const { processed, flagAmounts: amounts } = processFlagCountForDisplay(
      flagsCount,
      behaviorMappingActions
    );
    updateFlagCount(processed);
    setFlagAmounts(amounts);
  }, [flagsCount, setFlagAmounts, updateFlagCount, behaviorMappingActions]);

  useEffect(() => {
    if (keywordCount) {
      updateKeywordCount(keywordCount);
    }
  }, [keywordCount, updateKeywordCount]);

  useEffect(() => {
    if (topicCount) {
      updateTopicCount?.(topicCount);
    }
  }, [topicCount, updateTopicCount]);

  function print() {
    if (window.zE) {
      window.zE("messenger", "hide");
    }
    // Set up an event listener to show the widget again after printing
    window.addEventListener("afterprint", () => {
      if (window.zE) {
        window.zE("messenger", "show");
      }
    });

    setTimeout(() => {
      window.print();
    }, 1000);
  }

  function loadPosts(resetPage: boolean = false) {
    let pageQuery = page;
    if (resetPage) {
      setPage(1);
      setFilteredPostsList([]);
      pageQuery = 0;
    }
    return getPostsAsync({
      ...requestParametersSummary,
      page_size: pageQuery === 0 ? INITIAL_PAGE_SIZE : pageSize,
      page_number: pageQuery + 1,
    }).then((posts) => {
      if (pageQuery === 0) {
        setPage(LOAD_MORE_INITIAL_PAGE); // because of the page_size increment the next page for the load more start after 5
      }
      if (posts?.length === 0) {
        setShouldLoadMore(false);
        return;
      }
      if (resetPage) {
        setFilteredPostsList(posts);
        return;
      }
      setFilteredPostsList((prev) => [...(prev ?? []), ...(posts ?? [])]);
    });
  }

  function loadMore() {
    loadPosts().then(() => {
      setPage((prev) => prev + 1);
    });
  }

  useEffect(() => {
    if (isSuccessMark) {
      navigate("/reports/pending_review");
    }
  }, [isSuccessMark, navigate]);

  useEffect(() => {
    if (amendmentData && canNavAmendment) {
      navigate("/reports/amendment", { state: { stepDone: true } });
      return;
    }
    if (amendmentData && !canNavAmendment) {
      navigate("/reports");
    }
  }, [amendmentData, canNavAmendment, navigate, navigateToReports]);

  useEffect(() => {
    if (!filteredPostsList) return;
    const newPostList: { [key: string]: ItemWithMetaData[] } = {};
    const itemsWithDate: ItemWithMetaData[] = [];
    const itemsWithoutDate: ItemWithMetaData[] = [];

    filteredPostsList.forEach((item) => {
      const createdDate = item.item.content?.created_at;
      if (createdDate) {
        itemsWithDate.push(item);
      } else {
        itemsWithoutDate.push(item);
      }
    });

    if (sort.id === ItemsForReviewSortCriteria.Date) {
      itemsWithDate.forEach((item) => {
        const createdDate = new Date(
          item.item.content?.created_at
        ).getFullYear();
        if (!(createdDate in newPostList)) {
          newPostList[createdDate] = [];
        }
        newPostList[createdDate].push(item);
      });

      if (itemsWithoutDate.length > 0) {
        newPostList["No Date"] = itemsWithoutDate; // Add items without date at the end
      }
    } else {
      var mapPostList = new Map();
      filteredPostsList.forEach((item) => {
        mapPostList.set(item.item.external_id, item);
      });
      mapPostList.forEach((item) => {
        let haveBehavior: boolean = false;
        item.rating.forEach((rating: any) => {
          if (rating.score_type === "behavior") {
            haveBehavior = true;
            if (!(rating.name in newPostList)) {
              newPostList[rating.name] = [];
            }
            newPostList[rating.name].push(item);
          }
        });
        if (!haveBehavior) {
          if (!("No Behavior" in newPostList)) {
            newPostList["No Behavior"] = [];
          }
          newPostList["No Behavior"].push(item);
        }
      });
      setPostListCategory(newPostList);
    }

    setPostListCategory(newPostList);
  }, [filteredPostsList, sort]);

  const categorySorted = (keyList: string[]) => {
    if (sort.id === ItemsForReviewSortCriteria.Date) {
      // Sort with "No Date" at the end
      return keyList.sort((a, b) => {
        if (a === "No Date") return 1;
        if (b === "No Date") return -1;
        return b.localeCompare(a);
      });
    }
    return keyList.sort();
  };

  const donutChartValues = Object.keys(flagAmounts).map((key) => {
    return {
      flagCount: flagAmounts[key],
      value: (flagAmounts[key] / (flagsCount?.behaviors.total ?? 1)) * 100,
      color: getColorCode(key) || "gray",
      label: key,
    };
  });

  function handleAmendment() {
    show(
      "Open Amendment",
      "Are you sure you want to open an amendment? Any changes made will overwrite the original report and cannot be undone.",
      (action: ConfirmDialogActions) => {
        if (action === "continue" && report.report_uuid) {
          openAmendment(report.report_uuid);
        }
        hide();
      }
    );
  }

  function exitHandler() {
    // Directly navigate to reports without showing confirmation dialog
    navigateToReports();
  }

  const popMenuReport = (report: ReportSummaryItemApi) => {
    let _items = [
      {
        component: renderReportMenuItem(CheckIcon, "Complete Review"),
        onClick: () => {
          markReviews([report.workflow_run_uuid]);
        },
      },
    ];
    if (canRegeneratePDF) {
      _items.push({
        component: renderReportMenuItem(DocumentIcon, "PDF Regeneration"),
        onClick: () => {
          regeneratePDF(report.report_uuid);
        },
      });
    }
    if (canRerunReport) {
      _items.push({
        component: renderReportMenuItem(
          ArrowPathIcon,
          "Rerun Person",
          "text-sm"
        ),
        onClick: () => {
          navigateCreatePerson({
            state: { rerun: true, parent_report: cleanReportData(report) },
          });
        },
      });
    }
    if (person.canCreateRecur) {
      _items.push({
        component: renderReportMenuItem(
          ArrowPathRoundedSquareIcon,
          "Setup Employee Rescreening",
          "text-sm"
        ),
        onClick: () => {
          setRecurReport(report);
          setIsRecurOpen(true);
        },
      });
    }
    if (canAmend) {
      _items.push({
        component: renderReportMenuItem(
          ShieldExclamationIcon,
          "Open Amendment",
          "text-sm"
        ),
        onClick: () => {
          handleAmendment();
        },
      });
    }
    return _items;
  };

  return (
    <>
      <div
        className={classNames(
          summaryOnly
            ? "w-full"
            : "bg-white p-6 rounded-xl shadow shadow-gray-300 print:p-0 print:shadow-none w-full"
        )}
      >
        <div>
          {!summaryOnly && (
            <>
              <div className="print:hidden flex flex-row justify-between gap-1">
                <div className="font-bold my-3 text-2xl">Completed Report</div>
                <div className="flex align-center items-center gap-3">
                  <span
                    className="cursor-pointer text-xs font-medium"
                    onClick={exitHandler}
                  >
                    Back to List
                  </span>
                  <div className="flex items-center gap-2">
                    {canPrintCurrentView && (
                      <Button
                        type={ButtonTypes.print}
                        text="Download Current View"
                        colorType={ButtonColorTypes.outline}
                        onClick={print}
                        disabled={
                          isLoadingProfiles || flagsLoading || postsLoading
                        }
                      />
                    )}
                    <PDFDownloadWrapper report_uuid={report?.report_uuid ?? ""}>
                      <Button
                        type={ButtonTypes.default}
                        text="Download Report"
                        colorType={ButtonColorTypes.success}
                        onClick={() => {}}
                      />
                    </PDFDownloadWrapper>
                    <div className="ml-2 pt-1.5 flex items-center">
                      {loadingMarkReviews && <LoadingSpinner />}
                      {!loadingMarkReviews && (
                        <PopMenu
                          button={<ElipseIcon />}
                          menuItems={popMenuReport(report)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {report.recur && report.recur === "recur" && (
                <div className="flex border rounded-lg border-green-600 mt-2 mb-5 text-sm text-gray-600 p-3 items-center gap-3">
                  <ClipboardIcon className="text-gray-500 w-6"></ClipboardIcon>
                  <span>
                    This report is part of Fama Employee Rescreening and
                    contains only new item(s) since the last report. To view
                    previous reports or manage subscription
                    <span
                      className="cursor-pointer text-sky-500"
                      onClick={() => {
                        navigate(
                          `/companies/${report.company_uuid}/settings/subscriptions`
                        );
                      }}
                    >
                      &nbsp;click here
                    </span>
                    .
                  </span>
                </div>
              )}
              {isRerunReport(report, reportReruns) && (
                <ReportsSummaryRerunBanner
                  report={report}
                  reportReruns={reportReruns}
                />
              )}
            </>
          )}
          <div className="print:hidden">
            {errorAmendment && (
              <ErrorMessageContainer message="Error Opening Amendment" />
            )}
            {errorUnlockingReport && (
              <ErrorMessageContainer message="Error Unlocking the report, please try again" />
            )}
            {errorMarkReviews && (
              <ErrorMessageContainer message="Error marking report as review" />
            )}
            {errorGeneratePDF && (
              <ErrorMessageContainer message="Error generating the PDF please try again later or contact support." />
            )}
          </div>
          {postError && <ErrorMessageContainer message="Error loading posts" />}
          {errorFlag && <ErrorMessageContainer message="Error loading flags" />}
          {errorTopicCount && (
            <ErrorMessageContainer message="Error loading topic counts" />
          )}

          {!postError &&
            !errorFlag &&
            !errorUnlockingReport &&
            !errorAmendment && (
              <>
                {(isLoadingProfiles ||
                  flagsLoading ||
                  keywordCountLoading ||
                  topicCountLoading ||
                  !profileList) &&
                !errorFlag &&
                !errorUnlockingReport &&
                !errorAmendment &&
                !errorKeywordCount &&
                !errorTopicCount ? (
                  <LoadingSpinner className="relative" />
                ) : (
                  <ReportsSummaryHeader
                    hasReruns={
                      !!(
                        rerunParentUuid ||
                        (reportReruns?.length && reportReruns.length > 1)
                      )
                    }
                    flagsCount={flagsCount}
                    analyticsFlagsCount={flagsCount}
                    donutChartValues={donutChartValues}
                    flagAmounts={flagAmounts}
                    profileList={profileList}
                    keywordCount={keywordCount}
                    topicCount={topicCount}
                    companyUUID={report.company_uuid}
                    hideTabs={summaryOnly}
                  />
                )}

                <div className="flex flex-nowrap justify-between print:hidden">
                  <div className="font-bold my-3 text-2xl">All Posts</div>
                  <div>
                    <DropdownCombo
                      items={[
                        {
                          id: ItemsForReviewSortCriteria.Date,
                          label: "Sort By Date",
                        },
                        {
                          id: ItemsForReviewSortCriteria.Behavior,
                          label: "Sort By Behavior",
                        },
                      ]}
                      onChange={(e) => {
                        setSort(e);
                      }}
                      name="sort-by-dropdown"
                      initial={sort}
                    ></DropdownCombo>
                  </div>
                </div>

                <hr className="print:hidden" />
                <div className="my-4">
                  <ReportsSummaryPosts
                    categorySorted={categorySorted}
                    behaviorMappingActions={behaviorMappingActions}
                    postListCategory={postListCategory}
                    report={report}
                  />
                </div>
              </>
            )}
          {postsLoading && <LoadingSpinner />}
          {shouldLoadMore && !postsLoading && (
            <Button
              className="print:hidden"
              text={"Load More Posts"}
              onClick={() => loadMore()}
            ></Button>
          )}
        </div>

        {modal}
        {modalUnlock}
        {(loadingAmendment || loadingUnlockReport || loadingGeneratePDF) && (
          <LoadingSpinner />
        )}
        {recurReport && (
          <RecurCreateWrapper
            report={recurReport}
            isOpen={isRecurOpen}
            setModalOpen={setIsRecurOpen}
          />
        )}
      </div>
    </>
  );
};

export default ReportsSummary;

function cleanReportData(report: ReportSummaryItemApi): ReportSummaryItemApi {
  return {
    ...report,
  };
}
